import * as Phaser from 'phaser';
import { VideoPokerEngine } from '../engine/VideoPokerEngine';
import { Card, VideoPokerState, HandResult } from '../types/poker.types';
import { VideoPokerVariantSelector } from '../components/VideoPokerVariantSelector';
import { getCurrentVariant } from '../config/videopoker.config';
import { UserManager } from '../components/UserManager';
import { UserProfile, UserHighScore } from '../types/user.types';
import { ApiAuth, AuthenticatedUser } from '../components/ApiAuth';
import { SoundControlsPanel } from '../components/SoundControlsPanel';

export class VideoPokerScene extends Phaser.Scene {
    private engine: VideoPokerEngine;
    private cardSprites: Phaser.GameObjects.Container[] = [];
    private heldTexts: Phaser.GameObjects.Text[] = [];
    private dealButton!: Phaser.GameObjects.Container;
    private drawButton!: Phaser.GameObjects.Container;
    private betButton!: Phaser.GameObjects.Container;
    private maxBetButton!: Phaser.GameObjects.Container;
    private lastToggleTime: number[] = [0, 0, 0, 0, 0]; // Track last toggle time for each card
    
    // UI elements
    private balanceText!: Phaser.GameObjects.Text;
    private betText!: Phaser.GameObjects.Text;
    private winText!: Phaser.GameObjects.Text;
    private holdInstructionText!: Phaser.GameObjects.Text;
    private handResultText!: Phaser.GameObjects.Text;
    private backButton!: Phaser.GameObjects.Container;
    private variantSelector?: VideoPokerVariantSelector;
    private soundControls?: SoundControlsPanel;
    private userManager: UserManager;

    // Authentication and Stats System
    private auth: ApiAuth | null = null;
    private currentUser: AuthenticatedUser | null = null;
    private playerRankings: any[] = [];
    private rankingTexts: Phaser.GameObjects.Text[] = [];
    private playerStatsTexts: Phaser.GameObjects.Text[] = [];
    private nameInputText!: Phaser.GameObjects.Text;

    // Player stats tracking
    private playerStats = {
        handsPlayed: 0,
        bestHand: '',
        biggestWin: 0,
        totalWon: 0,
        totalLost: 0
    };
    
    // Paytable elements for dynamic updates
    private paytableElements!: {
        handBgs: Phaser.GameObjects.Graphics[],
        colBgs: Phaser.GameObjects.Graphics[],
        handTexts: Phaser.GameObjects.Text[],
        payoutTexts: Phaser.GameObjects.Text[][]
    };

    // Particle systems for card sparkles
    private cardParticleEmitters: Phaser.GameObjects.Particles.ParticleEmitter[] = [];

    constructor() {
        super({ key: 'VideoPokerScene' });
        this.engine = new VideoPokerEngine();
        this.userManager = UserManager.getInstance();

        // Initialize authentication
        this.auth = ApiAuth.getInstance();
        this.currentUser = this.auth.getUser();

        // Load player rankings
        this.loadPlayerRankings();

        // Set initial balance from UserManager (not ApiAuth)
        const user = this.userManager.getCurrentUser();
        if (user?.balance !== undefined) {
            this.engine.setBalance(user.balance);
            console.log('Video Poker create() - Loading balance from UserManager:', user.balance);
        }

        // Load player stats
        this.loadPlayerStats();
    }

    create(): void {
        const { width, height } = this.cameras.main;

        // Optimize for mobile touch
        if (this.sys.game.device.input.touch) {
            // Disable context menu on long press
            this.input.mouse?.disableContextMenu();

            // Set touch handling
            this.input.addPointer(2); // Support multi-touch
            this.input.topOnly = false; // Allow overlapping interactions

            // iOS Safari specific settings
            if (this.sys.game.device.os.iOS) {
                // Enable touch actions for better iOS compatibility
                const canvas = this.sys.game.canvas;
                canvas.style.touchAction = 'manipulation';
                canvas.style.webkitTouchCallout = 'none';
                canvas.style.webkitUserSelect = 'none';
                canvas.style.webkitTapHighlightColor = 'transparent';

                // Prevent double-tap zoom on iOS
                let lastTouchTime = 0;
                canvas.addEventListener('touchend', (e) => {
                    const now = Date.now();
                    if (now - lastTouchTime <= 300) {
                        e.preventDefault();
                    }
                    lastTouchTime = now;
                }, false);
            }
        }

        // Create particle texture first, before anything else
        this.createSparkTexture();

        this.createBackground();
        this.createTable();
        this.createCards();
        this.createPaytable();
        this.createUI();
        this.createControls();
        this.createPlayerPanel();
        this.createHighScoreBoard();
        this.createVariantSelector();
        this.createSoundControls();
        this.updateUI();

        // Deal initial hand for display only - will be reshuffled on actual bet
        const initialHand = this.engine.dealForDisplay();
        this.updateCards(initialHand);
        this.updateButtonVisibility();
    }

    private createSparkTexture(): void {
        // Create a simple white dot texture for particles
        if (!this.textures.exists('spark')) {
            const graphics = this.add.graphics();
            graphics.fillStyle(0xFFFFFF, 1);
            graphics.fillCircle(2, 2, 2);
            graphics.generateTexture('spark', 4, 4);
            graphics.destroy();
        }
    }

    private createBackground(): void {
        const { width, height } = this.cameras.main;
        
        // Classic casino machine blue background - matching example
        const bg = this.add.graphics();
        bg.fillStyle(0x003366, 1); // Deep blue like classic video poker
        bg.fillRect(0, 0, width, height);
    }

    private createTable(): void {
        const { width, height } = this.cameras.main;
        
        // Game title (will be part of paytable now)
        // Removed separate title as it's integrated into paytable

        // Back to menu button
        this.backButton = this.createButton(50, 30, 'MENU', () => {
            this.scene.start('MenuScene');
        });
    }

    private createCards(): void {
        const { width } = this.cameras.main;
        const cardWidth = 120; // Slightly wider like example
        const cardHeight = 170; // Taller proportion like example
        const spacing = 140;
        const startX = (width - (spacing * 4)) / 2;
        const cardY = 370; // Balanced position with good clearance from paytable

        for (let i = 0; i < 5; i++) {
            const x = startX + (i * spacing);
            
            // Card container
            const container = this.add.container(x, cardY);
            
            // Card background - clean white with thin black border
            const cardBg = this.add.graphics();
            cardBg.fillStyle(0xFFFFFF, 1);
            cardBg.fillRoundedRect(-cardWidth/2, -cardHeight/2, cardWidth, cardHeight, 6);
            cardBg.lineStyle(2, 0x000000, 1);
            cardBg.strokeRoundedRect(-cardWidth/2, -cardHeight/2, cardWidth, cardHeight, 6);
            
            // Top-left rank - larger like example
            const topRank = this.add.text(-cardWidth/2 + 8, -cardHeight/2 + 6, '', {
                fontFamily: 'Arial',
                fontSize: '32px',
                color: '#000000',
                fontStyle: 'bold'
            }).setOrigin(0, 0);
            
            // Top-left suit - positioned closer under rank
            const topSuit = this.add.text(-cardWidth/2 + 8, -cardHeight/2 + 38, '', {
                fontFamily: 'Arial',
                fontSize: '28px',
                color: '#000000'
            }).setOrigin(0, 0);
            
            // Center large suit symbol - much bigger like example
            const centerSuit = this.add.text(0, 0, '', {
                fontFamily: 'Arial',
                fontSize: '80px',
                color: '#000000'
            }).setOrigin(0.5, 0.5);
            
            // HELD text - position below the card
            const heldText = this.add.text(0, 0, 'HELD', {
                fontFamily: 'Arial',
                fontSize: '16px',
                color: '#FFFF00',
                fontStyle: 'bold',
                stroke: '#000000',
                strokeThickness: 3
            });
            heldText.setPosition(0, cardHeight/2 + 15); // Below the card
            heldText.setOrigin(0.5, 0.5);
            heldText.setVisible(false); // Start hidden, show only when held
            heldText.setDepth(1000); // Ensure it's on top
            
            // Card back graphics
            const cardBack = this.add.graphics();
            this.createCardBackPattern(cardBack, cardWidth, cardHeight);
            cardBack.setVisible(false); // Hidden by default
            
            container.add([cardBg, topRank, topSuit, centerSuit, cardBack, heldText]);
            // Make interactive area match card size exactly to reduce sensitivity
            // Touch devices already have sufficient hit area
            const touchPadding = 5; // Smaller padding to reduce accidental taps
            container.setInteractive(
                new Phaser.Geom.Rectangle(
                    -cardWidth/2 - touchPadding,
                    -cardHeight/2 - touchPadding,
                    cardWidth + (touchPadding * 2),
                    cardHeight + (touchPadding * 2)
                ),
                Phaser.Geom.Rectangle.Contains
            );

            // Ensure cards are above any highlights in the paytable or background
            container.setDepth(100);

            // Set input to use hand cursor on desktop
            if (!this.sys.game.device.input.touch) {
                container.input!.cursor = 'pointer';
            }
            
            // Hover effect only for non-touch devices
            container.on('pointerover', () => {
                const state = this.engine.getState();
                if (state.gamePhase === 'deal' && !this.sys.game.device.input.touch) {
                    container.setScale(1.05);
                }
            });
            
            container.on('pointerout', () => {
                if (!this.sys.game.device.input.touch) {
                    container.setScale(1.0);
                }
            });
            
            // For touch devices, use pointerup to avoid accidental taps
            // For desktop, use pointerdown for immediate response
            if (this.sys.game.device.input.touch) {
                container.on('pointerup', (pointer: Phaser.Input.Pointer) => {
                    // Make sure this is a tap, not a drag
                    if (pointer.getDistance() < 10) {
                        console.log('Touch tap on card', i);
                        this.toggleHold(i);
                    }
                });
            } else {
                // Desktop uses pointerdown for immediate response
                container.on('pointerdown', () => {
                    console.log('Mouse click on card', i);
                    this.toggleHold(i);
                });
            }
            
            this.cardSprites.push(container);
            this.heldTexts.push(heldText);

            // Create particle emitter for this card but keep it inactive initially
            try {
                const particles = this.add.particles(0, 0, 'spark', {
                    // Position around card edges
                    emitZone: {
                        type: 'edge',
                        source: new Phaser.Geom.Rectangle(-60, -85, 120, 170),
                        quantity: 48,
                        yoyo: false
                    },
                    // Particle behavior
                    scale: { start: 0.5, end: 0, ease: 'Quad.easeOut' },
                    blendMode: 'ADD',
                    speed: { min: 20, max: 50 },
                    lifespan: { min: 500, max: 1000 },
                    frequency: 50, // How often particles are emitted (ms)
                    quantity: 1, // Particles per emission
                    tint: 0xFFFFFF, // Will be changed based on card type
                    alpha: { start: 1, end: 0, ease: 'Quad.easeIn' }
                });

                // Add to container and track emitter
                container.add(particles);
                particles.stop(); // Start inactive
                this.cardParticleEmitters.push(particles.emitters.list[0]);
            } catch (error) {
                console.warn('Failed to create particle emitter for card', i, error);
                // Add null placeholder to maintain index alignment
                this.cardParticleEmitters.push(null as any);
            }
        }
    }

    private createCardBackPattern(graphics: Phaser.GameObjects.Graphics, cardWidth: number, cardHeight: number): void {
        // Clear any existing graphics
        graphics.clear();
        
        // Card background - blue with border
        graphics.fillStyle(0x000066, 1);
        graphics.fillRoundedRect(-cardWidth/2, -cardHeight/2, cardWidth, cardHeight, 6);
        graphics.lineStyle(2, 0x000000, 1);
        graphics.strokeRoundedRect(-cardWidth/2, -cardHeight/2, cardWidth, cardHeight, 6);
        
        // Create a classic diamond pattern
        graphics.lineStyle(2, 0x6666FF, 1);
        
        // Draw diamond grid pattern
        const spacing = 20;
        for (let x = -cardWidth/2 + spacing; x < cardWidth/2; x += spacing) {
            for (let y = -cardHeight/2 + spacing; y < cardHeight/2; y += spacing) {
                // Draw small diamond
                graphics.beginPath();
                graphics.moveTo(x, y - 8);
                graphics.lineTo(x + 8, y);
                graphics.lineTo(x, y + 8);
                graphics.lineTo(x - 8, y);
                graphics.closePath();
                graphics.strokePath();
            }
        }
        
        // Add central design
        graphics.fillStyle(0x6666FF, 1);
        graphics.fillCircle(0, 0, 15);
        graphics.fillStyle(0x000066, 1);
        graphics.fillCircle(0, 0, 10);
    }
    
    private getPaytableDisplayData(variantConfig: any): Array<{name: string, payouts: number[]}> {
        const paytable = variantConfig.paytable;
        const hands = [];
        const HandType = {
            ROYAL_FLUSH: 'royal_flush',
            STRAIGHT_FLUSH: 'straight_flush',
            FOUR_OF_A_KIND: 'four_of_a_kind',
            FULL_HOUSE: 'full_house',
            FLUSH: 'flush',
            STRAIGHT: 'straight',
            THREE_OF_A_KIND: 'three_of_a_kind',
            TWO_PAIR: 'two_pair',
            PAIR: 'pair',
            HIGH_CARD: 'high_card',
            FIVE_OF_A_KIND: 'five_of_a_kind',
            WILD_ROYAL_FLUSH: 'wild_royal_flush',
            FOUR_DEUCES: 'four_deuces'
        };

        // Show variant-specific hands in proper order
        if (variantConfig.id === 'deuces_wild') {
            // Deuces Wild specific order
            if (paytable[HandType.ROYAL_FLUSH] && paytable[HandType.ROYAL_FLUSH].some((p: number) => p > 0)) {
                hands.push({ name: 'NATURAL ROYAL', payouts: paytable[HandType.ROYAL_FLUSH] });
            }
            if (paytable[HandType.FOUR_DEUCES] && paytable[HandType.FOUR_DEUCES].some((p: number) => p > 0)) {
                hands.push({ name: 'FOUR DEUCES', payouts: paytable[HandType.FOUR_DEUCES] });
            }
            if (paytable[HandType.WILD_ROYAL_FLUSH] && paytable[HandType.WILD_ROYAL_FLUSH].some((p: number) => p > 0)) {
                hands.push({ name: 'WILD ROYAL', payouts: paytable[HandType.WILD_ROYAL_FLUSH] });
            }
            if (paytable[HandType.FIVE_OF_A_KIND] && paytable[HandType.FIVE_OF_A_KIND].some((p: number) => p > 0)) {
                hands.push({ name: 'FIVE OF A KIND', payouts: paytable[HandType.FIVE_OF_A_KIND] });
            }
        } else {
            // Standard variants - show Royal Flush
            if (paytable[HandType.ROYAL_FLUSH] && paytable[HandType.ROYAL_FLUSH].some((p: number) => p > 0)) {
                hands.push({ name: 'ROYAL FLUSH', payouts: paytable[HandType.ROYAL_FLUSH] });
            }
        }

        // Standard hands (common to all)
        if (paytable[HandType.STRAIGHT_FLUSH] && paytable[HandType.STRAIGHT_FLUSH].some((p: number) => p > 0)) {
            hands.push({ name: 'STRAIGHT FLUSH', payouts: paytable[HandType.STRAIGHT_FLUSH] });
        }

        // Handle bonus quad variants specially
        if (variantConfig.specialRules && variantConfig.specialRules.bonusQuads) {
            const bonusQuads = variantConfig.specialRules.bonusQuads;

            if (variantConfig.id === 'bonus_poker') {
                // Bonus Poker specific quad breakdown
                if (bonusQuads[14]) {
                    hands.push({ name: 'FOUR ACES', payouts: bonusQuads[14] });
                }
                if (bonusQuads[2]) {
                    hands.push({ name: 'FOUR 2s-4s', payouts: bonusQuads[2] });
                }
                // Base four of a kind for other ranks
                hands.push({ name: '4 OF A KIND', payouts: paytable[HandType.FOUR_OF_A_KIND] });

            } else if (variantConfig.id === 'double_bonus_poker') {
                // Double Bonus Poker specific quad breakdown
                if (bonusQuads[14]) {
                    hands.push({ name: 'FOUR ACES', payouts: bonusQuads[14] });
                }
                if (bonusQuads[2]) {
                    hands.push({ name: 'FOUR 2s-4s', payouts: bonusQuads[2] });
                }
                if (bonusQuads[5]) {
                    hands.push({ name: 'FOUR 5s-Ks', payouts: bonusQuads[5] });
                }
            } else {
                // Standard four of a kind
                hands.push({ name: '4 OF A KIND', payouts: paytable[HandType.FOUR_OF_A_KIND] });
            }
        } else {
            // Standard four of a kind for variants without bonus quads
            if (paytable[HandType.FOUR_OF_A_KIND] && paytable[HandType.FOUR_OF_A_KIND].some((p: number) => p > 0)) {
                hands.push({ name: '4 OF A KIND', payouts: paytable[HandType.FOUR_OF_A_KIND] });
            }
        }
        if (paytable[HandType.FULL_HOUSE] && paytable[HandType.FULL_HOUSE].some((p: number) => p > 0)) {
            hands.push({ name: 'FULL HOUSE', payouts: paytable[HandType.FULL_HOUSE] });
        }
        if (paytable[HandType.FLUSH] && paytable[HandType.FLUSH].some((p: number) => p > 0)) {
            hands.push({ name: 'FLUSH', payouts: paytable[HandType.FLUSH] });
        }
        if (paytable[HandType.STRAIGHT] && paytable[HandType.STRAIGHT].some((p: number) => p > 0)) {
            hands.push({ name: 'STRAIGHT', payouts: paytable[HandType.STRAIGHT] });
        }
        if (paytable[HandType.THREE_OF_A_KIND] && paytable[HandType.THREE_OF_A_KIND].some((p: number) => p > 0)) {
            hands.push({ name: '3 OF A KIND', payouts: paytable[HandType.THREE_OF_A_KIND] });
        }
        if (paytable[HandType.TWO_PAIR] && paytable[HandType.TWO_PAIR].some((p: number) => p > 0)) {
            hands.push({ name: 'TWO PAIR', payouts: paytable[HandType.TWO_PAIR] });
        }
        if (paytable[HandType.PAIR] && paytable[HandType.PAIR].some((p: number) => p > 0)) {
            const pairName = variantConfig.id === 'jacks_or_better' || variantConfig.id === 'bonus_poker' || variantConfig.id === 'double_bonus_poker'
                ? 'JACKS OR BETTER'
                : 'PAIR';
            hands.push({ name: pairName, payouts: paytable[HandType.PAIR] });
        }

        return hands;
    }

    private createPaytable(): void {
        const { width } = this.cameras.main;
        const tableY = 60;
        const tableHeight = 200;
        const tableWidth = width - 100;
        const startX = 50;
        
        // Main paytable background - classic blue border
        const border = this.add.graphics();
        border.lineStyle(4, 0x0066FF, 1);
        border.strokeRect(startX, tableY, tableWidth, tableHeight);
        border.setData('paytable', true); // Mark for cleanup
        
        // Store references for dynamic updates
        this.paytableElements = {
            handBgs: [],
            colBgs: [],
            handTexts: [],
            payoutTexts: []
        };
        
        // Hand names column - transparent background
        const handsColWidth = 180;
        
        // Bet amount columns - all same color initially  
        const payoutColWidth = (tableWidth - handsColWidth - 8) / 5;
        
        for (let i = 0; i < 5; i++) {
            const colX = startX + handsColWidth + 4 + (i * payoutColWidth);
            
            // Column background - transparent by default
            const colBg = this.add.graphics();
            // Start transparent, only show red when it's the current bet
            this.paytableElements.colBgs.push(colBg);
            
            // Bet amount header
            const betHeader = this.add.text(colX + payoutColWidth/2, tableY + 15, (i + 1).toString(), {
                fontFamily: 'Arial',
                fontSize: '16px',
                color: '#FFFFFF',
                fontStyle: 'bold',
                stroke: '#000000',
                strokeThickness: 1
            }).setOrigin(0.5);
            betHeader.setData('paytable', true); // Mark for cleanup
        }
        
        // Hand types and payouts - dynamic based on variant
        const variantConfig = getCurrentVariant();
        const hands = this.getPaytableDisplayData(variantConfig);
        
        // Calculate proper row spacing based on number of hands
        const rowSpacing = Math.min(18, (tableHeight - 50) / hands.length);

        hands.forEach((hand, i) => {
            const rowY = tableY + 35 + (i * rowSpacing);

            // Row background for highlighting wins
            const handBg = this.add.graphics();
            handBg.setVisible(false); // Hidden by default
            this.paytableElements.handBgs.push(handBg);

            // Hand name
            const handText = this.add.text(startX + 10, rowY, hand.name, {
                fontFamily: 'Arial',
                fontSize: '12px',
                color: '#FFDD00',
                fontStyle: 'bold',
                stroke: '#000000',
                strokeThickness: 1
            });
            this.paytableElements.handTexts.push(handText);

            // Payouts
            const payoutRow: Phaser.GameObjects.Text[] = [];
            hand.payouts.forEach((payout, j) => {
                const colX = startX + handsColWidth + 4 + (j * payoutColWidth);
                const payoutText = this.add.text(colX + payoutColWidth/2, rowY, payout.toString(), {
                    fontFamily: 'Arial',
                    fontSize: '12px',
                    color: '#FFFFFF',
                    fontStyle: 'bold',
                    stroke: '#000000',
                    strokeThickness: 1
                }).setOrigin(0.5, 0); // Changed origin to top-center for better alignment
                payoutRow.push(payoutText);
            });
            this.paytableElements.payoutTexts.push(payoutRow);
        });
        
        // Title above paytable - shows current variant
        const title = this.add.text(width / 2, tableY - 25, variantConfig.name.toUpperCase(), {
            fontFamily: 'Arial',
            fontSize: '20px',
            color: '#0066FF',
            fontStyle: 'bold',
            stroke: '#FFFFFF',
            strokeThickness: 2
        }).setOrigin(0.5);
        title.setData('paytable', true); // Mark for cleanup
    }

    private createUI(): void {
        const { width, height } = this.cameras.main;

        // WIN/BET/CREDITS display - positioned close below cards to reduce gap
        const statusY = 570; // Fixed position close to cards (cards end at ~540)
        
        // WIN area (left)
        this.add.text(150, statusY, 'WIN', {
            fontFamily: 'Arial',
            fontSize: '16px',
            color: '#FFFF00',
            fontStyle: 'bold',
            stroke: '#000000',
            strokeThickness: 2
        }).setOrigin(0.5);
        
        this.winText = this.add.text(150, statusY + 25, '0', {
            fontFamily: 'Arial',
            fontSize: '20px',
            color: '#FFFF00',
            fontStyle: 'bold',
            stroke: '#000000',
            strokeThickness: 2
        });
        this.winText.setOrigin(0.5);


        // BET area (center)
        this.add.text(width / 2, statusY, 'BET', {
            fontFamily: 'Arial',
            fontSize: '16px',
            color: '#FFFF00',
            fontStyle: 'bold',
            stroke: '#000000',
            strokeThickness: 2
        }).setOrigin(0.5);
        
        this.betText = this.add.text(width / 2, statusY + 25, '1', {
            fontFamily: 'Arial',
            fontSize: '20px',
            color: '#FFFF00',
            fontStyle: 'bold',
            stroke: '#000000',
            strokeThickness: 2
        });
        this.betText.setOrigin(0.5);

        // CREDITS area (right)
        this.add.text(width - 150, statusY, 'CREDITS', {
            fontFamily: 'Arial',
            fontSize: '16px',
            color: '#FFFF00',
            fontStyle: 'bold',
            stroke: '#000000',
            strokeThickness: 2
        }).setOrigin(0.5);
        
        this.balanceText = this.add.text(width - 150, statusY + 25, '1000', {
            fontFamily: 'Arial',
            fontSize: '20px',
            color: '#FFFF00',
            fontStyle: 'bold',
            stroke: '#000000',
            strokeThickness: 2
        });
        this.balanceText.setOrigin(0.5);
        console.log('Created balanceText at position:', width - 150, statusY + 25);

        // Hand result text - positioned proportionally between cards and status
        this.handResultText = this.add.text(width / 2, 545, '', {
            fontFamily: 'Arial',
            fontSize: '20px',
            color: '#FFDD00',
            fontStyle: 'bold',
            stroke: '#000000',
            strokeThickness: 2
        });
        this.handResultText.setOrigin(0.5);

        // Hold instruction text - positioned higher than hand result text to avoid conflicts
        this.holdInstructionText = this.add.text(width / 2, 515, '', {
            fontFamily: 'Arial',
            fontSize: '18px',
            color: '#FFD700',
            fontStyle: 'bold',
            stroke: '#000000',
            strokeThickness: 2
        });
        this.holdInstructionText.setOrigin(0.5);
        this.holdInstructionText.setVisible(false); // Initially hidden
    }

    private createControls(): void {
        const { width, height } = this.cameras.main;
        const buttonY = 635; // Centered in button bar at Y:610-660

        // Dark blue bar at bottom for buttons (thinner)
        const bottomBar = this.add.graphics();
        bottomBar.fillStyle(0x001122, 1); // Dark blue like classic video poker
        bottomBar.fillRect(0, 610, width, 50);

        // Center the three buttons horizontally under the BET field
        const buttonSpacing = 110; // Space between buttons

        // BET button on the left
        this.betButton = this.createCasinoButton(width / 2 - buttonSpacing, buttonY, 'BET', 0xFFDD00, () => {
            const currentBet = this.engine.getState().bet;
            const nextBet = currentBet >= 5 ? 1 : currentBet + 1;
            this.engine.setBet(nextBet);
            this.updateUI();
        });

        // Deal/Draw button in the CENTER (most prominent)
        this.dealButton = this.createLargeButton(width / 2, buttonY, 'DEAL', 0xFFDD00, () => this.deal());
        this.drawButton = this.createLargeButton(width / 2, buttonY, 'DRAW', 0xFFDD00, () => this.draw());

        // Max Bet button on the right
        this.maxBetButton = this.createCasinoButton(width / 2 + buttonSpacing, buttonY, 'MAX BET', 0xFFDD00, () => {
            this.engine.setBet(5);
            this.updateUI(); // Update UI to show correct bet amount
            this.deal();
        });
        this.drawButton.setVisible(false);
    }

    private createPlayerPanel(): void {
        const { height } = this.cameras.main;
        const x = 20;
        const y = height - 360; // Position to avoid overlap with paytable
        const width = 250;

        // Background
        const bg = this.add.graphics();
        bg.fillStyle(0x000000, 0.8);
        bg.fillRoundedRect(x, y, width, 200, 10);
        bg.lineStyle(2, 0x00FFFF, 1);
        bg.strokeRoundedRect(x, y, width, 200, 10);

        // Title
        const title = this.add.text(x + width / 2, y + 25, 'PLAYER INFO', {
            fontFamily: 'Orbitron',
            fontSize: '16px',
            color: '#00FFFF'
        });
        title.setOrigin(0.5);

        // Player name
        const displayName = this.currentUser ? this.currentUser.name : 'Guest';
        this.nameInputText = this.add.text(x + width / 2, y + 60, displayName, {
            fontFamily: 'Orbitron',
            fontSize: '18px',
            color: '#FFFFFF'
        });
        this.nameInputText.setOrigin(0.5);

        // Player stats display
        const statsTitle = this.add.text(x + width / 2, y + 90, 'YOUR STATS', {
            fontFamily: 'Orbitron',
            fontSize: '14px',
            color: '#FFD700'
        });
        statsTitle.setOrigin(0.5);

        // Create stats text elements
        const statLabels = ['Hands Played:', 'Best Hand:', 'Total Won:'];
        for (let i = 0; i < statLabels.length; i++) {
            const statText = this.add.text(x + 20, y + 115 + (i * 20), '', {
                fontFamily: 'Courier',
                fontSize: '12px',
                color: '#FFFFFF'
            });
            this.playerStatsTexts.push(statText);
        }

        this.updatePlayerStatsDisplay();
    }

    private createHighScoreBoard(): void {
        const { width, height } = this.cameras.main;
        const boardWidth = 180;
        const boardHeight = 200; // Make it shorter 
        const x = width - boardWidth - 20;
        const y = height - 360; // Position to avoid overlap with paytable

        // Background
        const bg = this.add.graphics();
        bg.fillStyle(0x000000, 0.8);
        bg.fillRoundedRect(x, y, boardWidth, boardHeight, 10);
        bg.lineStyle(2, 0xFFD700, 1);
        bg.strokeRoundedRect(x, y, boardWidth, boardHeight, 10);

        // Title
        const title = this.add.text(x + boardWidth / 2, y + 25, 'PLAYER RANKINGS', {
            fontFamily: 'Orbitron',
            fontSize: '16px',
            color: '#FFD700'
        });
        title.setOrigin(0.5);

        // Ranking entries
        for (let i = 0; i < 5; i++) {
            const rankingText = this.add.text(x + 15, y + 50 + (i * 20), '', {
                fontFamily: 'Courier',
                fontSize: '11px',
                color: '#FFFFFF'
            });
            this.rankingTexts.push(rankingText);
        }

        this.updateRankingsDisplay();
    }
    
    private createVariantSelector(): void {
        // Only create if we're in video poker mode and don't already have one
        if (!this.variantSelector) {
            this.variantSelector = new VideoPokerVariantSelector();

            // Listen for variant changes
            window.addEventListener('variantChanged', this.handleVariantChange.bind(this) as EventListener);
        }
    }

    private createSoundControls(): void {
        if (!this.soundControls) {
            this.soundControls = new SoundControlsPanel(this);
        }
    }
    
    private handleVariantChange(event: Event): void {
        const customEvent = event as CustomEvent;
        console.log('Variant changed to:', customEvent.detail.variant);

        // Preserve current balance and bet before recreating engine
        const currentBalance = this.engine.getState().balance;
        const currentBet = this.engine.getState().bet;

        // Recreate the engine with new variant
        this.engine = new VideoPokerEngine();

        // Restore the balance and bet that were lost during engine recreation
        this.engine.setBalance(currentBalance);
        this.engine.setBet(currentBet);

        // Update current user reference
        this.currentUser = this.userManager.getCurrentUser();
        
        // Refresh the paytable display
        this.refreshPaytable();
        
        // Reload player rankings for the new variant
        this.loadPlayerRankings();
        this.updateRankingsDisplay();
        
        // Reset UI
        this.updateUI();

        // Deal a fresh display hand - will be reshuffled on actual bet
        const displayHand = this.engine.dealForDisplay();
        this.updateCards(displayHand);
    }
    
    private refreshPaytable(): void {
        // Clear existing paytable elements
        if (this.paytableElements) {
            // Destroy all graphics and text elements
            this.paytableElements.handBgs.forEach(bg => bg.destroy());
            this.paytableElements.colBgs.forEach(bg => bg.destroy());
            this.paytableElements.handTexts.forEach(text => text.destroy());
            this.paytableElements.payoutTexts.forEach(row => {
                row.forEach(text => text.destroy());
            });
        }
        
        // Find and destroy other paytable elements (border, title, headers)
        this.children.list.filter(child => 
            child.getData && child.getData('paytable') === true
        ).forEach(child => child.destroy());
        
        // Recreate paytable
        this.createPaytable();
    }

    private createButton(x: number, y: number, text: string, callback: () => void): Phaser.GameObjects.Container {
        const container = this.add.container(x, y);
        
        // Bright colored button like casino machines
        const bg = this.add.graphics();
        bg.fillStyle(0xFF6600, 1); // Bright orange
        bg.fillRoundedRect(-45, -18, 90, 36, 8);
        bg.lineStyle(3, 0xFFFFFF, 1);
        bg.strokeRoundedRect(-45, -18, 90, 36, 8);
        
        // Inner highlight
        const highlight = this.add.graphics();
        highlight.fillStyle(0xFFAA55, 1);
        highlight.fillRoundedRect(-42, -15, 84, 12, 6);

        const buttonText = this.add.text(0, 0, text, {
            fontFamily: 'Arial',
            fontSize: '14px',
            color: '#000000',
            fontStyle: 'bold'
        });
        buttonText.setOrigin(0.5);

        container.add([bg, highlight, buttonText]);
        container.setInteractive(new Phaser.Geom.Rectangle(-45, -18, 90, 36), Phaser.Geom.Rectangle.Contains);
        container.on('pointerdown', callback);

        return container;
    }

    private createBetButton(x: number, y: number, text: string, callback: () => void): Phaser.GameObjects.Container {
        const container = this.add.container(x, y);
        
        const bg = this.add.graphics();
        bg.fillStyle(0xFFFF00, 1); // Yellow
        bg.fillRoundedRect(-25, -18, 50, 36, 8);
        bg.lineStyle(3, 0x000000, 1);
        bg.strokeRoundedRect(-25, -18, 50, 36, 8);

        const buttonText = this.add.text(0, 0, text, {
            fontFamily: 'Arial',
            fontSize: '18px',
            color: '#000000',
            fontStyle: 'bold'
        });
        buttonText.setOrigin(0.5);

        container.add([bg, buttonText]);
        container.setInteractive(new Phaser.Geom.Rectangle(-25, -18, 50, 36), Phaser.Geom.Rectangle.Contains);
        container.on('pointerdown', callback);

        return container;
    }

    private createRedButton(x: number, y: number, text: string, callback: () => void): Phaser.GameObjects.Container {
        const container = this.add.container(x, y);
        
        const bg = this.add.graphics();
        bg.fillStyle(0xFF0000, 1); // Red
        bg.fillRoundedRect(-45, -18, 90, 36, 8);
        bg.lineStyle(3, 0xFFFFFF, 1);
        bg.strokeRoundedRect(-45, -18, 90, 36, 8);

        const buttonText = this.add.text(0, 0, text, {
            fontFamily: 'Arial',
            fontSize: '14px',
            color: '#FFFFFF',
            fontStyle: 'bold'
        });
        buttonText.setOrigin(0.5);

        container.add([bg, buttonText]);
        container.setInteractive(new Phaser.Geom.Rectangle(-45, -18, 90, 36), Phaser.Geom.Rectangle.Contains);
        container.on('pointerdown', callback);

        return container;
    }

    private createGreenButton(x: number, y: number, text: string, callback: () => void): Phaser.GameObjects.Container {
        const container = this.add.container(x, y);
        
        const bg = this.add.graphics();
        bg.fillStyle(0x00AA00, 1); // Green
        bg.fillRoundedRect(-40, -18, 80, 36, 8);
        bg.lineStyle(3, 0xFFFFFF, 1);
        bg.strokeRoundedRect(-40, -18, 80, 36, 8);

        const buttonText = this.add.text(0, 0, text, {
            fontFamily: 'Arial',
            fontSize: '14px',
            color: '#FFFFFF',
            fontStyle: 'bold'
        });
        buttonText.setOrigin(0.5);

        container.add([bg, buttonText]);
        container.setInteractive(new Phaser.Geom.Rectangle(-40, -18, 80, 36), Phaser.Geom.Rectangle.Contains);
        container.on('pointerdown', callback);

        return container;
    }

    private createBlueButton(x: number, y: number, text: string, callback: () => void): Phaser.GameObjects.Container {
        const container = this.add.container(x, y);
        
        const bg = this.add.graphics();
        bg.fillStyle(0x0066FF, 1); // Blue
        bg.fillRoundedRect(-50, -18, 100, 36, 8);
        bg.lineStyle(3, 0xFFFFFF, 1);
        bg.strokeRoundedRect(-50, -18, 100, 36, 8);

        const buttonText = this.add.text(0, 0, text, {
            fontFamily: 'Arial',
            fontSize: '14px',
            color: '#FFFFFF',
            fontStyle: 'bold'
        });
        buttonText.setOrigin(0.5);

        container.add([bg, buttonText]);
        container.setInteractive(new Phaser.Geom.Rectangle(-50, -18, 100, 36), Phaser.Geom.Rectangle.Contains);
        container.on('pointerdown', callback);

        return container;
    }

    private toggleHold(index: number): void {
        // Debounce to prevent duplicate calls, increased for touch devices
        const now = Date.now();
        const debounceTime = this.sys.game.device.input.touch ? 200 : 50;
        if (now - this.lastToggleTime[index] < debounceTime) {
            console.log('Debounced duplicate toggle for card', index);
            return;
        }
        this.lastToggleTime[index] = now;

        const state = this.engine.getState();
        console.log('toggleHold called for card', index, 'Current phase:', state.gamePhase);

        if (state.gamePhase !== 'deal') {
            console.log('WARNING: Cannot hold cards - not in deal phase! Current phase:', state.gamePhase);
            // Provide visual feedback for invalid interaction
            const container = this.cardSprites[index];
            this.tweens.add({
                targets: container,
                scaleX: 0.95,
                scaleY: 0.95,
                duration: 100,
                yoyo: true,
                ease: 'Power2.easeInOut'
            });
            return;
        }

        this.engine.holdCard(index);
        const newState = this.engine.getState();
        console.log('After holdCard - Held cards:', newState.heldCards);
        
        // Get the HELD text from the container (it's the 6th element, index 5)
        const container = this.cardSprites[index];
        const heldText = container.list[5] as Phaser.GameObjects.Text;
        
        if (heldText) {
            const isHeld = newState.heldCards[index];
            
            // Kill any existing tweens on this container to prevent conflicts
            this.tweens.killTweensOf(container);
            
            // Set the held state immediately
            heldText.setVisible(isHeld);
            console.log('Setting HELD text', index, 'visible:', isHeld);
            
            // Also add visual feedback to the card
            if (isHeld) {
                container.setAlpha(0.8); // Slightly transparent when held
                // Brief scale animation for positive feedback - but don't use yoyo on mobile
                if (!this.sys.game.device.input.touch) {
                    this.tweens.add({
                        targets: container,
                        scaleX: 1.1,
                        scaleY: 1.1,
                        duration: 150,
                        yoyo: true,
                        ease: 'Power2.easeInOut'
                    });
                }
            } else {
                container.setAlpha(1.0); // Full opacity when not held
                // Brief scale animation for release feedback - but don't use on mobile
                if (!this.sys.game.device.input.touch) {
                    this.tweens.add({
                        targets: container,
                        scaleX: 0.9,
                        scaleY: 0.9,
                        duration: 100,
                        yoyo: true,
                        ease: 'Power2.easeInOut'
                    });
                }
            }
            
            // Re-evaluate current hand whenever cards are held/unheld
            this.evaluateCurrentHand();
        } else {
            console.log('ERROR: Could not find HELD text in container for card', index);
        }
    }

    private deal(): void {
        const hand = this.engine.deal();

        // Show hold instruction in dedicated text element
        this.holdInstructionText.setText('Choose hold cards');
        this.holdInstructionText.setVisible(true);

        // Clear hand result text and paytable highlighting
        this.handResultText.setText('');
        this.clearWinningHandHighlight();
        
        // Update card data first
        this.updateCards(hand);
        
        // Animate all cards flipping with staggered delays
        for (let i = 0; i < 5; i++) {
            this.animateCardFlip(i, i * 100); // 100ms delay between each card
        }
        
        // After animation completes, evaluate and show initial hand
        this.time.delayedCall(600, () => {
            this.evaluateCurrentHand();
        });
        
        this.updateUI();
        this.updateButtonVisibility();
    }

    private draw(): void {
        // Clear the preview text and highlights immediately when drawing
        this.handResultText.setText('');
        this.holdInstructionText.setVisible(false); // Hide hold instruction
        this.clearWinningHandHighlight();
        this.clearCardHighlights();

        const result = this.engine.draw();
        const heldCards = this.engine.getState().heldCards;

        // Update card data first
        this.updateCards(result.hand);

        // Animate only the non-held cards
        let animationDelay = 0;
        for (let i = 0; i < 5; i++) {
            if (!heldCards[i]) {
                this.animateCardFlip(i, animationDelay);
                animationDelay += 100; // Stagger only the flipping cards
            }
        }

        // Display result after animations complete
        this.time.delayedCall(animationDelay + 300, async () => {
            this.displayResult(result.result);
            this.updateUI();

            // Track hand statistics
            this.trackHandStats(result.result);

            // Save balance to database
            await this.saveBalance(result.result);

            // Return to betting phase but keep results and cards displayed
            this.clearHolds(); // Clear all HELD indicators
            this.engine.newGame(); // Reset engine to betting phase
            this.updateButtonVisibility(); // Show betting buttons
        });
    }

    private async saveBalance(result: HandResult): Promise<void> {
        const state = this.engine.getState();
        const won = result.payout > 0 ? result.payout - state.bet : 0;
        const lost = result.payout > 0 ? 0 : state.bet;

        console.log(`Video Poker: Saving balance ${state.balance}, won: ${won}, lost: ${lost}`);
        await this.userManager.updateBalance(state.balance, won, lost);
    }

    private newGame(): void {
        this.engine.newGame();
        this.clearCards();
        this.clearHolds();
        this.handResultText.setText('');
        this.handResultText.setColor('#FFDD00'); // Reset to default color
        this.holdInstructionText.setVisible(false); // Hide hold instruction
        this.clearWinningHandHighlight();
        this.clearCardHighlights();
        this.updateUI();
        this.updateButtonVisibility();
    }

    private updateCards(hand: Card[]): void {
        const state = this.engine.getState();
        const variantConfig = getCurrentVariant();
        const isDeucesWild = variantConfig.id === 'deuces_wild';

        hand.forEach((card, i) => {
            const container = this.cardSprites[i];
            const cardBg = container.list[0] as Phaser.GameObjects.Graphics;
            const topRank = container.list[1] as Phaser.GameObjects.Text;
            const topSuit = container.list[2] as Phaser.GameObjects.Text;
            const centerSuit = container.list[3] as Phaser.GameObjects.Text;
            const cardBack = container.list[4] as Phaser.GameObjects.Graphics;
            const heldText = container.list[5] as Phaser.GameObjects.Text;

            // Update card display
            const suit = card.suit;
            const rank = this.getDisplayRank(card.rank);
            const isWild = isDeucesWild && card.rank === 2;
            const color = (suit === 'hearts' || suit === 'diamonds') ? '#FF0000' : '#000000';
            const suitSymbol = this.getSuitSymbol(suit);

            // Special styling for wild cards (2s in Deuces Wild)
            if (isWild) {
                // Redraw card background with special wild card appearance
                cardBg.clear();

                // Purple/violet gradient background for wild cards
                cardBg.fillStyle(0xE6E6FA, 1); // Lavender background
                cardBg.fillRoundedRect(-60, -85, 120, 170, 6);

                // Purple border for wild cards
                cardBg.lineStyle(3, 0x8A2BE2, 1); // Blue violet border
                cardBg.strokeRoundedRect(-60, -85, 120, 170, 6);

                // Add inner glow effect
                cardBg.lineStyle(1, 0xDDA0DD, 0.7); // Plum inner glow
                cardBg.strokeRoundedRect(-58, -83, 116, 166, 5);

                // Set text to purple for wild cards
                topRank.setText(rank);
                topRank.setColor('#8A2BE2'); // Blue violet
                topRank.setStroke('#FFFFFF', 2); // White stroke for contrast
                topSuit.setText(suitSymbol);
                topSuit.setColor('#8A2BE2');
                centerSuit.setText(suitSymbol);
                centerSuit.setColor('#8A2BE2');

                // Add "WILD" text overlay with better contrast
                if (!container.getByName('wildText')) {
                    const wildText = this.add.text(0, 35, 'WILD', {
                        fontFamily: 'Arial',
                        fontSize: '22px',
                        color: '#FF1493', // Deep pink
                        fontStyle: 'bold',
                        stroke: '#FFFFFF',
                        strokeThickness: 3
                    });
                    wildText.setOrigin(0.5, 0.5);
                    wildText.setAngle(-20);
                    wildText.setName('wildText');
                    container.add(wildText);
                } else {
                    container.getByName('wildText').setVisible(true);
                }
            } else {
                // Normal card appearance for non-wild cards
                cardBg.clear();

                // Different background for Deuces Wild variant (slight blue tint)
                if (isDeucesWild) {
                    cardBg.fillStyle(0xF0F8FF, 1); // Alice blue for Deuces Wild
                } else {
                    cardBg.fillStyle(0xFFFFFF, 1); // Pure white for other variants
                }
                cardBg.fillRoundedRect(-60, -85, 120, 170, 6);
                cardBg.lineStyle(2, 0x000000, 1);
                cardBg.strokeRoundedRect(-60, -85, 120, 170, 6);

                // Normal text colors
                topRank.setText(rank);
                topRank.setColor(color);
                topRank.setStroke('', 0); // Remove stroke
                topSuit.setText(suitSymbol);
                topSuit.setColor(color);
                centerSuit.setText(suitSymbol);
                centerSuit.setColor(color);

                // Hide wild text if it exists
                const wildText = container.getByName('wildText');
                if (wildText) {
                    wildText.setVisible(false);
                }
            }

            // For now, just show the face immediately (we'll add animation next)
            cardBack.setVisible(false);
            topRank.setVisible(true);
            topSuit.setVisible(true);
            centerSuit.setVisible(true);

            // IMPORTANT: Preserve the held state when updating cards
            if (state.gamePhase === 'deal' && state.heldCards[i]) {
                heldText.setVisible(true);
                container.setAlpha(0.8);
            }

            // Update particle effects based on card type
            const emitter = this.cardParticleEmitters[i];
            if (emitter && typeof emitter.stop === 'function') {
                if (isWild) {
                    // Wild cards get purple/pink sparkles
                    try {
                        emitter.stop();
                        emitter.setConfig({
                            tint: [0xFF1493, 0x8A2BE2, 0xDDA0DD], // Deep pink, blue violet, plum
                            frequency: 30, // More frequent for wild cards
                            quantity: 2,
                            scale: { start: 0.6, end: 0, ease: 'Quad.easeOut' }
                        });
                        emitter.start();
                    } catch (error) {
                        console.warn('Failed to configure wild card particles:', error);
                    }
                } else {
                    // Stop sparkles for non-wild cards (will be reactivated if winning)
                    try {
                        emitter.stop();
                    } catch (error) {
                        console.warn('Failed to stop particles:', error);
                    }
                }
            }
        });
    }
    
    private animateCardFlip(cardIndex: number, delay: number = 0): void {
        const container = this.cardSprites[cardIndex];
        const topRank = container.list[1] as Phaser.GameObjects.Text;
        const topSuit = container.list[2] as Phaser.GameObjects.Text;
        const centerSuit = container.list[3] as Phaser.GameObjects.Text;
        const cardBack = container.list[4] as Phaser.GameObjects.Graphics;
        const wildText = container.getByName('wildText') as Phaser.GameObjects.Text;

        // Start with card back showing
        cardBack.setVisible(true);
        topRank.setVisible(false);
        topSuit.setVisible(false);
        centerSuit.setVisible(false);
        if (wildText) {
            wildText.setVisible(false);
        }

        // Scale flip animation
        this.time.delayedCall(delay, () => {
            // First half: scale down to 0 (hiding back)
            this.tweens.add({
                targets: container,
                scaleX: 0,
                duration: 150,
                ease: 'Power2.easeIn',
                onComplete: () => {
                    // Switch to face
                    cardBack.setVisible(false);
                    topRank.setVisible(true);
                    topSuit.setVisible(true);
                    centerSuit.setVisible(true);

                    // Show wild text if it's a wild card
                    const variantConfig = getCurrentVariant();
                    const isDeucesWild = variantConfig.id === 'deuces_wild';
                    const rank = topRank.text;
                    if (isDeucesWild && rank === '2' && wildText) {
                        wildText.setVisible(true);
                    }

                    // Second half: scale back up (showing face)
                    this.tweens.add({
                        targets: container,
                        scaleX: 1,
                        duration: 150,
                        ease: 'Power2.easeOut'
                    });
                }
            });
        });
    }

    private clearCards(): void {
        this.cardSprites.forEach(container => {
            const topRank = container.list[1] as Phaser.GameObjects.Text;
            const topSuit = container.list[2] as Phaser.GameObjects.Text;
            const centerSuit = container.list[3] as Phaser.GameObjects.Text;
            const cardBack = container.list[4] as Phaser.GameObjects.Graphics;

            topRank.setText('');
            topSuit.setText('');
            centerSuit.setText('');
            cardBack.setVisible(false);
        });
    }

    private showCardBacks(): void {
        // Show card backs to indicate cards are present but face-down
        this.cardSprites.forEach(container => {
            const topRank = container.list[1] as Phaser.GameObjects.Text;
            const topSuit = container.list[2] as Phaser.GameObjects.Text;
            const centerSuit = container.list[3] as Phaser.GameObjects.Text;
            const cardBack = container.list[4] as Phaser.GameObjects.Graphics;

            // Hide the face of the cards
            topRank.setText('');
            topSuit.setText('');
            centerSuit.setText('');

            // Show the card back
            cardBack.setVisible(true);
        });
    }

    private clearHolds(): void {
        // Clear holds from all card containers
        this.cardSprites.forEach(container => {
            const heldText = container.list[5] as Phaser.GameObjects.Text; // Now at index 5
            if (heldText) {
                heldText.setVisible(false);
            }
            // Reset card transparency back to normal
            container.setAlpha(1.0);
        });
    }

    private displayResult(result: HandResult): void {
        if (result.payout > 0) {
            this.handResultText.setText(result.description + ' - WIN $' + result.payout);
            this.handResultText.setColor('#FFD700'); // Gold color for actual wins
            this.highlightWinningHand(result.description);
        } else {
            this.handResultText.setText('No Win');
            this.handResultText.setColor('#FF0000'); // Red for no win
            this.clearWinningHandHighlight();
        }
    }

    private updateUI(): void {
        const state = this.engine.getState();
        console.log('updateUI called with state:', { balance: state.balance, bet: state.bet, lastWin: state.lastWin });
        
        if (this.balanceText) {
            this.balanceText.setText(state.balance.toString());
            console.log('Set balance text to:', state.balance.toString());
        } else {
            console.log('ERROR: balanceText is null');
        }
        
        if (this.betText) {
            this.betText.setText(state.bet.toString());
            console.log('Set bet text to:', state.bet.toString());
        } else {
            console.log('ERROR: betText is null');
        }
        
        if (this.winText) {
            this.winText.setText(state.lastWin.toString());
            console.log('Set win text to:', state.lastWin.toString());
        } else {
            console.log('ERROR: winText is null');
        }
    }

    private updateButtonVisibility(): void {
        const state = this.engine.getState();
        
        this.dealButton.setVisible(state.gamePhase === 'betting');
        this.drawButton.setVisible(state.gamePhase === 'deal');
        this.maxBetButton.setVisible(state.gamePhase === 'betting');
        this.betButton.setVisible(state.gamePhase === 'betting');

        // Manage hold instruction text visibility
        if (state.gamePhase === 'deal') {
            this.holdInstructionText.setVisible(true);
        } else {
            this.holdInstructionText.setVisible(false);
        }
        
        // Update paytable highlighting
        this.updatePaytableHighlighting();
    }
    
    private updatePaytableHighlighting(): void {
        const state = this.engine.getState();
        const currentBet = state.bet;
        
        // Clear all columns and only highlight current bet column
        this.paytableElements.colBgs.forEach((colBg, i) => {
            colBg.clear();
            const isCurrentBet = (i + 1) === currentBet;
            
            if (isCurrentBet) {
                // Only draw red background for current bet column
                const { width } = this.cameras.main;
                const tableWidth = width - 100;
                const handsColWidth = 180;
                const payoutColWidth = (tableWidth - handsColWidth - 8) / 5;
                const startX = 50;
                const tableY = 60;
                const tableHeight = 200;
                const colX = startX + handsColWidth + 4 + (i * payoutColWidth);
                
                colBg.fillStyle(0xFF0000, 1);
                colBg.fillRect(colX, tableY + 4, payoutColWidth, tableHeight - 8);
                colBg.lineStyle(1, 0x000000, 1);
                colBg.strokeRect(colX, tableY + 4, payoutColWidth, tableHeight - 8);
            }
            // Other columns remain transparent (no drawing)
        });
    }
    
    private highlightWinningHand(handType: string): void {
        // Find the matching hand in the paytable by text comparison
        const handTexts = this.paytableElements.handTexts;

        // Standardize the hand type for comparison
        const standardizedHandType = handType.toUpperCase()
            .replace('NATURAL ', '') // Remove "Natural" prefix
            .replace('WILD ', ''); // Remove "Wild" prefix

        handTexts.forEach((handText, index) => {
            const handName = handText.text.toUpperCase();

            // Check for various matches
            let match = false;

            // Direct match
            if (handName === standardizedHandType) {
                match = true;
            }
            // Special cases
            else if (standardizedHandType === 'ROYAL FLUSH' &&
                    (handName === 'ROYAL FLUSH' || handName === 'NATURAL ROYAL' || handName === 'WILD ROYAL')) {
                match = true;
            }
            else if (standardizedHandType === 'FOUR 2S' && handName === 'FOUR DEUCES') {
                match = true;
            }
            else if (standardizedHandType === 'FOUR OF A KIND' && handName === '4 OF A KIND') {
                match = true;
            }
            else if (standardizedHandType.includes('FOUR') && standardizedHandType.includes('ACE') && handName === 'FOUR ACES') {
                match = true;
            }
            else if (standardizedHandType.includes('FOUR') &&
                    (standardizedHandType.includes('2') || standardizedHandType.includes('3') || standardizedHandType.includes('4')) &&
                    handName === 'FOUR 2S-4S') {
                match = true;
            }
            else if (standardizedHandType.includes('FOUR') &&
                    (standardizedHandType.includes('5') || standardizedHandType.includes('6') || standardizedHandType.includes('7') ||
                     standardizedHandType.includes('8') || standardizedHandType.includes('9') || standardizedHandType.includes('10') ||
                     standardizedHandType.includes('JACK') || standardizedHandType.includes('QUEEN') || standardizedHandType.includes('KING')) &&
                    handName === 'FOUR 5S-KS') {
                match = true;
            }
            else if (standardizedHandType === 'THREE OF A KIND' && handName === '3 OF A KIND') {
                match = true;
            }
            else if (standardizedHandType.includes('PAIR') && handName === 'JACKS OR BETTER' &&
                    (standardizedHandType.includes('JACK') || standardizedHandType.includes('QUEEN') ||
                     standardizedHandType.includes('KING') || standardizedHandType.includes('ACE'))) {
                match = true;
            }

            if (match) {
                // Highlight this row
                handText.setColor('#FFFFFF');
                handText.setFontStyle('bold');
                handText.setStroke('#000000', 2);
            }
        });
    }
    
    private clearWinningHandHighlight(): void {
        // Reset all hand texts to normal yellow appearance
        this.paytableElements.handTexts.forEach(handText => {
            handText.setColor('#FFDD00');
            handText.setFontStyle('bold'); // Keep bold
            handText.setStroke('#000000', 1); // Reset to normal stroke
        });
    }

    private getDisplayRank(rank: number): string {
        switch (rank) {
            case 14: return 'A';
            case 13: return 'K';
            case 12: return 'Q';
            case 11: return 'J';
            default: return rank.toString();
        }
    }

    private getSuitSymbol(suit: string): string {
        switch (suit) {
            case 'hearts': return '♥';
            case 'diamonds': return '♦';
            case 'clubs': return '♣';
            case 'spades': return '♠';
            default: return '';
        }
    }

    // Player rankings and stats system
    private async loadPlayerRankings(): Promise<void> {
        try {
            const apiAuth = ApiAuth.getInstance();
            const serverUrl = apiAuth.getApiKey() ? apiAuth['adminServerUrl'] : 'http://localhost:3334';
            const response = await fetch(`${serverUrl}/api/rankings`);
            if (response.ok) {
                this.playerRankings = await response.json();
            }
        } catch (error) {
            console.warn('Failed to load player rankings:', error);
            this.playerRankings = [];
        }
    }

    private async loadPlayerStats(): Promise<void> {
        try {
            if (!this.currentUser) return;

            const apiAuth = ApiAuth.getInstance();
            const serverUrl = apiAuth.getAdminServerUrl();
            const apiKey = apiAuth.getApiKey();

            // Re-validate to get updated user data with stats
            const response = await fetch(`${serverUrl}/api/validate/${apiKey}`);
            if (response.ok) {
                const data = await response.json();
                if (data.valid && data.user.stats) {
                    this.playerStats = {
                        handsPlayed: data.user.stats.handsPlayed || 0,
                        bestHand: data.user.stats.bestHand || '',
                        biggestWin: data.user.stats.biggestWin || 0,
                        totalWon: data.user.stats.totalWon || 0,
                        totalLost: data.user.stats.totalLost || 0
                    };
                    console.log('Loaded player stats:', this.playerStats);
                    // Update the display
                    this.updatePlayerStatsDisplay();
                }
            }
        } catch (error) {
            console.warn('Failed to load player stats:', error);
        }
    }

    private updateRankingsDisplay(): void {
        for (let i = 0; i < this.rankingTexts.length; i++) {
            if (i < this.playerRankings.length) {
                const player = this.playerRankings[i];
                const text = `${i + 1}. ${player.name.substring(0, 8)}: $${player.balance}`;
                this.rankingTexts[i].setText(text);
            } else {
                this.rankingTexts[i].setText('');
            }
        }
    }

    private updatePlayerStatsDisplay(): void {
        if (this.playerStatsTexts.length >= 3) {
            this.playerStatsTexts[0].setText(`Hands Played: ${this.playerStats.handsPlayed}`);
            this.playerStatsTexts[1].setText(`Best Hand: ${this.playerStats.bestHand || 'None'}`);
            this.playerStatsTexts[2].setText(`Total Won: $${this.playerStats.totalWon}`);
        }
    }

    private trackHandStats(result: HandResult): void {
        if (!this.currentUser) return;

        // Increment hands played
        this.playerStats.handsPlayed++;

        // Track total money won/lost
        if (result.payout > 0) {
            const currentBet = this.engine.getState().bet;
            const netProfit = result.payout - currentBet;

            // Only count net profit (not gross payout) for total won
            if (netProfit > 0) {
                this.playerStats.totalWon += netProfit;
            }

            // Check if this is the biggest win (use gross payout for this stat)
            if (result.payout > this.playerStats.biggestWin) {
                this.playerStats.biggestWin = result.payout;
            }

            // Check if this is the best hand (higher ranking hands are better)
            const handRanking = this.getHandRanking(result.description);
            const currentBestRanking = this.getHandRanking(this.playerStats.bestHand);
            if (handRanking > currentBestRanking) {
                this.playerStats.bestHand = result.description;
            }
        } else {
            this.playerStats.totalLost += this.engine.getState().bet;
        }

        // Update the display
        this.updatePlayerStatsDisplay();

        // Update server stats
        this.updateServerStats();
    }

    private getHandRanking(handDescription: string): number {
        const rankings: { [key: string]: number } = {
            'Royal Flush': 10,
            'Straight Flush': 9,
            'Four of a Kind': 8,
            'Full House': 7,
            'Flush': 6,
            'Straight': 5,
            'Three of a Kind': 4,
            'Two Pair': 3,
            'Jacks or Better': 2,
            'Pair': 1,
            'High Card': 0,
            '': 0 // Empty string default
        };
        return rankings[handDescription] || 0;
    }

    private async updateServerStats(): Promise<void> {
        if (!this.currentUser || !this.auth) return;

        const state = this.engine.getState();

        try {
            const response = await fetch(`${this.auth.getAdminServerUrl()}/api/users/${this.currentUser.id}/stats`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-api-key': this.auth.getApiKey() || ''
                },
                body: JSON.stringify({
                    hands_played: this.playerStats.handsPlayed,
                    best_hand: this.playerStats.bestHand,
                    biggest_win: this.playerStats.biggestWin,
                    balance: state.balance,
                    won: this.playerStats.totalWon,
                    lost: this.playerStats.totalLost
                })
            });

            if (response.ok) {
                console.log('Stats updated successfully');
            }
        } catch (error) {
            console.error('Failed to update stats:', error);
        }
    }

    private promptForName(): void {
        if (!this.currentUser || this.currentUser.id === 'guest') {
            alert('Please log in with a user account to change your name!');
            return;
        }
        
        const newDisplayName = prompt('Enter your display name:', this.currentUser.displayName);
        if (newDisplayName && newDisplayName.trim()) {
            const trimmedName = newDisplayName.trim().substring(0, 20);
            this.userManager.updateUserPreferences(this.currentUser.id, {});
            
            // Update the user's display name in their profile
            const users = this.userManager.getAllUsers();
            const userIndex = users.findIndex(u => u.id === this.currentUser!.id);
            if (userIndex >= 0) {
                users[userIndex].displayName = trimmedName;
                this.currentUser.displayName = trimmedName;
            }
            
            this.nameInputText.setText(trimmedName);
        }
    }
    

    private createCasinoButton(x: number, y: number, text: string, color: number, callback: () => void): Phaser.GameObjects.Container {
        const container = this.add.container(x, y);
        
        // Classic casino button style - yellow/gold like example
        const bg = this.add.graphics();
        bg.fillStyle(color, 1);
        bg.fillRoundedRect(-30, -15, 60, 30, 4);
        bg.lineStyle(2, 0x000000, 1);
        bg.strokeRoundedRect(-30, -15, 60, 30, 4);
        
        // Inner highlight for 3D effect
        const highlight = this.add.graphics();
        highlight.lineStyle(1, 0xFFFFFF, 0.8);
        highlight.strokeRoundedRect(-28, -13, 56, 26, 3);

        const buttonText = this.add.text(0, 0, text, {
            fontFamily: 'Arial',
            fontSize: '12px',
            color: '#000000',
            fontStyle: 'bold'
        });
        buttonText.setOrigin(0.5);

        container.add([bg, highlight, buttonText]);
        container.setInteractive(new Phaser.Geom.Rectangle(-30, -15, 60, 30), Phaser.Geom.Rectangle.Contains);
        container.on('pointerdown', callback);

        return container;
    }

    private evaluateCurrentHand(): void {
        // Get the current hand evaluation
        const result = this.engine.evaluateCurrentHand();
        
        // Display the current hand result
        if (result.payout > 0) {
            // Show what the player currently has
            this.handResultText.setText(`${result.description}`);
            this.handResultText.setColor('#00FF00'); // Green for winning hands
            
            // Highlight the winning hand in the paytable
            this.highlightWinningHand(result.description);
            
            // Highlight only the specific winning cards
            this.highlightWinningCards(result.winningCardIndices || []);
        } else {
            // No winning hand currently
            this.handResultText.setText('');
            this.handResultText.setColor('#FFDD00'); // Return to default color
            this.clearWinningHandHighlight();
            
            // Clear any card highlights
            this.clearCardHighlights();
        }
    }
    
    private highlightWinningCards(winningIndices: number[]): void {
        // First, reset all cards to normal appearance
        this.clearCardHighlights();

        const variantConfig = getCurrentVariant();
        const isDeucesWild = variantConfig.id === 'deuces_wild';

        // Then highlight only the winning cards
        winningIndices.forEach(index => {
            if (index >= 0 && index < this.cardSprites.length) {
                const container = this.cardSprites[index];
                const cardBg = container.list[0] as Phaser.GameObjects.Graphics;
                const topRank = container.list[1] as Phaser.GameObjects.Text;
                const isWild = isDeucesWild && topRank.text === '2';

                if (cardBg) {

                    cardBg.clear();

                    if (isWild) {
                        // Special winning wild card - brighter purple with gold accent
                        cardBg.fillStyle(0xFFE4E1, 1); // Misty rose background for winning wild
                        cardBg.fillRoundedRect(-60, -85, 120, 170, 6);
                        cardBg.lineStyle(4, 0xFFD700, 1); // Gold border for winning
                        cardBg.strokeRoundedRect(-60, -85, 120, 170, 6);
                        cardBg.lineStyle(2, 0xFF1493, 0.8); // Deep pink inner glow
                        cardBg.strokeRoundedRect(-58, -83, 116, 166, 5);
                    } else {
                        // Normal winning card highlight
                        cardBg.fillStyle(0xFFFFF0, 1); // Very light cream/yellow background
                        cardBg.fillRoundedRect(-60, -85, 120, 170, 6);
                        cardBg.lineStyle(3, 0xFFD700, 1); // Gold border for winning cards
                        cardBg.strokeRoundedRect(-60, -85, 120, 170, 6);
                    }
                }

                // Add golden sparkles for winning cards
                const emitter = this.cardParticleEmitters[index];
                if (emitter && typeof emitter.stop === 'function' && !isWild) { // Wild cards already have sparkles
                    try {
                        emitter.stop();
                        emitter.setConfig({
                            tint: [0xFFD700, 0xFFFACD, 0xFFFF00], // Gold, lemon chiffon, yellow
                            frequency: 40, // Moderate frequency for wins
                            quantity: 2,
                            scale: { start: 0.7, end: 0, ease: 'Quad.easeOut' },
                            speed: { min: 30, max: 60 }
                        });
                        emitter.start();
                    } catch (error) {
                        console.warn('Failed to configure winning card particles:', error);
                    }
                }
            }
        });
    }
    
    private clearCardHighlights(): void {
        // Reset all cards but preserve wild card styling
        const variantConfig = getCurrentVariant();
        const isDeucesWild = variantConfig.id === 'deuces_wild';

        this.cardSprites.forEach((container, index) => {
            const cardBg = container.list[0] as Phaser.GameObjects.Graphics;
            const topRank = container.list[1] as Phaser.GameObjects.Text;
            const isWild = isDeucesWild && topRank.text === '2';

            if (cardBg) {

                cardBg.clear();

                if (isWild) {
                    // Preserve wild card styling
                    cardBg.fillStyle(0xE6E6FA, 1); // Lavender background
                    cardBg.fillRoundedRect(-60, -85, 120, 170, 6);
                    cardBg.lineStyle(3, 0x8A2BE2, 1); // Blue violet border
                    cardBg.strokeRoundedRect(-60, -85, 120, 170, 6);
                    cardBg.lineStyle(1, 0xDDA0DD, 0.7); // Plum inner glow
                    cardBg.strokeRoundedRect(-58, -83, 116, 166, 5);
                } else {
                    // Normal card styling
                    if (isDeucesWild) {
                        cardBg.fillStyle(0xF0F8FF, 1); // Alice blue for Deuces Wild
                    } else {
                        cardBg.fillStyle(0xFFFFFF, 1); // Pure white for other variants
                    }
                    cardBg.fillRoundedRect(-60, -85, 120, 170, 6);
                    cardBg.lineStyle(2, 0x000000, 1);
                    cardBg.strokeRoundedRect(-60, -85, 120, 170, 6);
                }
            }

            // Stop sparkles for non-wild cards
            const emitter = this.cardParticleEmitters[index];
            if (emitter && typeof emitter.stop === 'function' && !isWild) {
                try {
                    emitter.stop();
                } catch (error) {
                    console.warn('Failed to stop particles in clearCardHighlights:', error);
                }
            }
        });
    }
    

    private createLargeButton(x: number, y: number, text: string, color: number, callback: () => void): Phaser.GameObjects.Container {
        const container = this.add.container(x, y);
        
        // Larger button for DEAL/DRAW
        const bg = this.add.graphics();
        bg.fillStyle(color, 1);
        bg.fillRoundedRect(-40, -20, 80, 40, 6);
        bg.lineStyle(3, 0x000000, 1);
        bg.strokeRoundedRect(-40, -20, 80, 40, 6);
        
        // Inner highlight for 3D effect
        const highlight = this.add.graphics();
        highlight.lineStyle(1, 0xFFFFFF, 0.8);
        highlight.strokeRoundedRect(-37, -17, 74, 34, 5);

        const buttonText = this.add.text(0, 0, text, {
            fontFamily: 'Arial',
            fontSize: '16px',
            color: '#000000',
            fontStyle: 'bold'
        });
        buttonText.setOrigin(0.5);

        container.add([bg, highlight, buttonText]);
        container.setInteractive(new Phaser.Geom.Rectangle(-40, -20, 80, 40), Phaser.Geom.Rectangle.Contains);
        container.on('pointerdown', callback);

        return container;
    }

    private createWideButton(x: number, y: number, text: string, color: number, callback: () => void): Phaser.GameObjects.Container {
        const container = this.add.container(x, y);
        
        // Wider button for longer text
        const bg = this.add.graphics();
        bg.fillStyle(color, 1);
        bg.fillRoundedRect(-50, -15, 100, 30, 4);
        bg.lineStyle(2, 0x000000, 1);
        bg.strokeRoundedRect(-50, -15, 100, 30, 4);
        
        // Inner highlight for 3D effect
        const highlight = this.add.graphics();
        highlight.lineStyle(1, 0xFFFFFF, 0.8);
        highlight.strokeRoundedRect(-48, -13, 96, 26, 3);

        const buttonText = this.add.text(0, 0, text, {
            fontFamily: 'Arial',
            fontSize: '11px',
            color: '#000000',
            fontStyle: 'bold'
        });
        buttonText.setOrigin(0.5);

        container.add([bg, highlight, buttonText]);
        container.setInteractive(new Phaser.Geom.Rectangle(-50, -15, 100, 30), Phaser.Geom.Rectangle.Contains);
        container.on('pointerdown', callback);

        return container;
    }
    
    private refreshPlayerBalance(): void {
        // Get current profile balance
        const user = this.userManager.getCurrentUser();

        if (!user) return;

        // Update engine balance to match profile balance
        const profileBalance = user.balance || 1000;
        this.engine.setBalance(profileBalance);

        // Update UI display
        this.updateUI();

        console.log(`Video Poker balance refreshed: $${profileBalance}`);
    }

    destroy(): void {
        if (this.variantSelector) {
            this.variantSelector.destroy();
            this.variantSelector = undefined;
        }
        if (this.soundControls) {
            this.soundControls.destroy();
            this.soundControls = undefined;
        }
    }

    shutdown(): void {
        if (this.variantSelector) {
            this.variantSelector.destroy();
            this.variantSelector = undefined;
        }
        if (this.soundControls) {
            this.soundControls.destroy();
            this.soundControls = undefined;
        }
    }

}